// TODO:
// - Improve mic fakeness: The Mic's responsiveness can be improved visually
// - verify hint works
// - and whole flow before deploying

/** Imports **/
import React, { createRef, useEffect, useState } from 'react';
import cx from 'classnames';
import ReactiveListeningStatus from '../Global/ReactiveListeningStatus/ReactiveListeningStatus';
import AmiraIconButton, { ICON_BUTTON_TYPES } from '../Global/AmiraIconButton/AmiraIconButton';
import Timer from '../DialogDisplay/Timer';
import IconOverlays from '../DialogDisplay/IconOverlays';
import { STATES, POINTER_IDS } from './constants';
import useInteractionController from './useInteractionController';
import BookOverlay from '../Global/BookOverlay/BookOverlay';
import useIsOnScreenKeyboardOpen from '../../hooks/useIsOnScreenKeyboardOpen';

/** Component **/
const Interaction = ({
  speech,
  onDone,
  interactions,
  storyId,
  phraseIndex,
  activityId,
  showFTUE,
  onFinishFTUE,
}) => {
  
  //#region Component State
  // We don't want to update the display UNTIL we say the prompt.  Kids will read the prompt before it's time otherwise
  const [displayPrompt, setDisplayPrompt] = useState("");

  // Showing / Hiding Elements (Especially used for FTUE)
  const [showPrompt, setShowPrompt] = useState(false);
  const [showStudentTurn, setShowStudentTurn] = useState(false);
  const [showTimer, setShowTimer] = useState(true);
  const [showMicrophone, setShowMicrohone] = useState(true);
  const [showSubmitButton, setShowSubmitButton] = useState(true);
  const [showRepeatButton, setShowRepeatButton] = useState(true);
  const [showHintButton, setShowHintButton] = useState(true);
  const [showExcerptsButton, setShowExcerptsButton] = useState(true);
  const [showPauseOverlay, setShowPauseOverlay] = useState(false);
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);
  const [showCheckmarkOverlay, setShowCheckmarkOverlay] = useState(false);

  const [submitButtonEnabled, setSubmitButtonEnabled] = useState(false);
  const [repeatButtonEnabled, setRepeatButtonEnabled] = useState(true);
  const [excerptsButtonEnabled, setExcerptsButtonEnabled] = useState(false);
  const [hintButtonEnabled, setHintButtonEnabled] = useState(true);

  const [activeFTUEPointer, setActiveFTUEPointer] = useState(null);  
  const [excerptsOverlayImageLoaded, setExcerptsOverlayImageLoaded] = useState(false);

  const textResponseInputRef = createRef();

  // This really grew, so put controller in a hook and separate file for readability, view stays here
  const {
    handleTextResponseChange,
    onClickSubmit,
    useTextInput,
    textResponse,
    submitTimerElapsed,
    volumeLevel,
    excerptsPage1,
    excerptsPage2,
    currentInteraction,
    getNoSubmitTimeout,
    onCloseExcerpts,
    onClickRepeat,
    onClickExcerpts,
    onClickHint,
    currentState,
    helpButtonsEnabled,
    currentHint,
    fakeActiveListeningStatus,
  } = useInteractionController({
    onDone, 
    onFinishFTUE, 
    speech,
    interactions,
    storyId,
    phraseIndex,
    activityId,
    showFTUE,
    setDisplayPrompt,
    setShowPrompt,
    setShowStudentTurn,
    setShowTimer,
    setShowMicrohone,
    setShowSubmitButton,
    setShowRepeatButton,
    setShowHintButton,
    setShowExcerptsButton,
    setActiveFTUEPointer,
    setSubmitButtonEnabled,
    setRepeatButtonEnabled,
    setExcerptsButtonEnabled,
    setHintButtonEnabled,
    setShowPauseOverlay,
    setShowLoadingOverlay,
    setShowCheckmarkOverlay,
  });

  const isOnScreenKeyboardOpen = useIsOnScreenKeyboardOpen(textResponseInputRef);

  useEffect(() => {
    speech.setOnScreenKeyboardOpen(isOnScreenKeyboardOpen);
  }, [isOnScreenKeyboardOpen])

  //#endregion

  //#region Render 
  const getPromptBlock = () => {
    return (
      <div className={cx('prompt', { invisible: !showPrompt })}>
        <div className='repeatButtonWrapper'>
          <AmiraIconButton
            onClick={onClickRepeat}
            disabled={!submitButtonEnabled || !repeatButtonEnabled}
            type={ICON_BUTTON_TYPES.REPEAT_AUDIO}
            invisible={!showRepeatButton}
            pointerPosition='right'
            showPointer={activeFTUEPointer === POINTER_IDS.REPEAT_BUTTON}
          />
        </div>
        {displayPrompt}
      </div>
    );
  };

  const getIconOverlays = () => {
    return (
      <>
        <IconOverlays
          showPauseOverlay={showPauseOverlay}
          showLoadingOverlay={showLoadingOverlay}
          showCheckmarkOverlay={showCheckmarkOverlay}
        />
      </>
    );
  };

  const getExcerptsOverlay = () => {
    return (
      <BookOverlay
        leftPageContent={excerptsPage1 && excerptsPage1.reduce((page, phrase) => `${page}${phrase}\n\n`, "")}
        rightPageContent={excerptsPage2 && excerptsPage2.reduce((page, phrase) => `${page}${phrase}\n\n`, "")}
        onClose={onCloseExcerpts}
      />
    );
  };

  const getButtonsColumn = () => {
    return (
      <div className='buttonsCol'>
        <div className='buttonsWrapper'>
          <div style={{ marginBottom: '10px' }}>
            <AmiraIconButton
              onClick={onClickExcerpts}
              disabled={!submitButtonEnabled || !excerptsButtonEnabled || !helpButtonsEnabled}
              type={ICON_BUTTON_TYPES.BOOK}
              invisible={!showExcerptsButton}
              pointerPosition='right'
              showPointer={activeFTUEPointer === POINTER_IDS.EXCERPTS_BUTTON}
            />
          </div>

          <AmiraIconButton
            onClick={onClickHint}
            disabled={!currentHint || !submitButtonEnabled || !hintButtonEnabled || !helpButtonsEnabled}
            type={ICON_BUTTON_TYPES.QUESTION_MARK}
            invisible={!showHintButton}
            pointerPosition='right'
            showPointer={activeFTUEPointer === POINTER_IDS.HINT_BUTTON}
          />
        </div>
      </div>
    );
  };

  const getInputColumn = () => (
    <div className='inputCol'>
      {useTextInput && (
        <div className={cx('textInputWrapper', {
          invisible: !showMicrophone,
          pointerTarget: true,
          left: true,
          showPointer: activeFTUEPointer === POINTER_IDS.TEXT_BOX
        })}
        >
          <textarea
            ref={textResponseInputRef}
            type='text'
            spellCheck="false"
            disabled={!submitButtonEnabled}
            onChange={handleTextResponseChange}
            value={textResponse}
            autoCapitalize="none"
            autoCorrect="off"
            aria-label="dialog item text response"
          />

        </div>
      )}
      {!useTextInput && (
        <div 
          className={cx('listeningStatusWrapper', {
            invisible: !showMicrophone,
            pointerTarget: true,
            left: true,
            showPointer: activeFTUEPointer === POINTER_IDS.MICROPHONE,
          })}
        >
          <ReactiveListeningStatus
            forceActiveState={fakeActiveListeningStatus}
            volumeLevel={volumeLevel ? volumeLevel : 0}
            large
          />
        </div>
      )}
      {getIconOverlays()}
    </div>
  );

  const getSubmitColumn = () => (
    <div className='submitCol'>
      <div className='submitButtonWrapper'>
        <AmiraIconButton
          onClick={onClickSubmit}
          disabled={!submitButtonEnabled}
          type={ICON_BUTTON_TYPES.SUBMIT}
          invisible={!showSubmitButton}
          pointerPosition='left'
          showPointer={activeFTUEPointer === POINTER_IDS.SUBMIT_BUTTOM}
          large
        />
      </div>
    </div>
  );

  const getTimer = () => (
    <div 
      className={cx('timerRow', {
        invisible: !showTimer,
        pointerTarget: true,
        left: true,
        showPointer: activeFTUEPointer === POINTER_IDS.TIMER,
      })}
    >
      <Timer
        submitTimerElapsed={submitTimerElapsed}
        getNoSubmitTimeout={getNoSubmitTimeout}
      />
    </div>
  );

  return (
    <div className="Interaction">
      {currentInteraction && (
        <>
          {getPromptBlock()}
          <div 
            className={cx('controlContainer', { 
              show: showStudentTurn,
              invisible: !showStudentTurn,
            })}
          >
            {currentState === STATES.HANDLE_EXCERPTS_CLICKED && getExcerptsOverlay()}
            {getTimer()}
            <div className='submitRow'>
              {getButtonsColumn()}
              {getInputColumn()}
              {getSubmitColumn()}
            </div>
          </div>
        </>
      )}
    </div>
  );
  //#endregion
};

export default Interaction;
